import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAssignAllUsersToGroupMutation } from '../../apiRtk/be4feApi';
import { FORM } from '../../config/consts';
import { useUrlParams } from '../../helpers/urlParams';
import actions from '../../modules/accessSettings';
import { getAccessGroupUsersAll } from '../../modules/accessSettings/selectors';
import types from '../../modules/accessSettings/types';
import { addSnackbarSuccess } from '../../modules/notifications/actions';
import { ActionModal } from '../_DesignSystemWrappers/Modal';
import UsersAutocomplete from '../UsersAutocomplete';
import { ADD_ALL_USERS_ID } from '../UsersAutocomplete/UsersAutocomplete';
import { useGetGlobalHooks } from './hooks/getGlobalHooks';
import messages from './messages';

function AccessGroupAddUsersModal({ groupId = '', onClose, onSuccess, open }) {
  const { dispatch, formatMessage, formMethods, handleSubmit, isDirty, isValid, users } = useGetGlobalHooks(
    FORM.HAS_FORM
  );
  const { companyId, siteId } = useUrlParams();
  const [assignAllUsersToGroup, { isLoading: isAssignAllUsersLoading }] = useAssignAllUsersToGroupMutation();
  const accessGroupCurrentUsers = useSelector(getAccessGroupUsersAll);
  const areAllUsersAdded = users.length === 0;

  useEffect(() => {
    dispatch(actions.getAccessControlUsers(groupId));
  }, []);

  function handleOnSubmit() {
    const data = formMethods.getValues();
    const isAllUsersSelected = data.users.some((item) => item.id === ADD_ALL_USERS_ID);

    if (isAllUsersSelected) {
      assignAllUsersToGroup({
        companyId,
        groupId: String(groupId),
        siteId,
      })
        .unwrap()
        .then(() => {
          dispatch(addSnackbarSuccess(messages.accessSnackbarGroupUpdatedSuccessfully));
          onSuccess();
          onClose();
        })
        .catch((error) => {
          dispatch({ context: null, error, type: types.ADD_ACCESS_GROUP_USER_FAILURE });
        });
      return;
    }

    const usersUUID = data.users
      .filter((item) => !(item.id === FORM.NEW_ITEM_ID || item.id === FORM.NEW_ITEM_WITHOUT_LOGIN_ID))
      .flatMap((item) => item.uuid);

    if (data.lastName) {
      const { email, firstName, lastName } = data;
      dispatch(actions.addAccessGroupUserWithCreate(groupId, { email, firstName, lastName }));
    }

    if (usersUUID.length) {
      if (accessGroupCurrentUsers.length) {
        usersUUID.push(...accessGroupCurrentUsers.map((user) => user.uuid));
      }
      dispatch(actions.addAccessGroupUsers(groupId, usersUUID));
    }

    onClose();
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <ActionModal
          icon={<AddIcon />}
          isPrimaryButtonDisabled={!isDirty || !isValid}
          muiDialogProps={{
            maxWidth: 'xs',
          }}
          onClose={onClose}
          open={open}
          isNotClosable={true}
          isPrimaryButtonLoading={isAssignAllUsersLoading}
          primaryButtonAction={handleSubmit(handleOnSubmit)}
          primaryButtonText={formatMessage(messages.accessGroupAddUserModalAdd)}
          secondaryButtonText={formatMessage(messages.accessGroupAddUserModalCancel)}
          title={formatMessage(messages.accessGroupAddUsersModalTitle)}
        >
          <Typography variant="body1">
            <FormattedMessage {...messages.accessGroupAddUserModalText} values={{ br: <br /> }} />
          </Typography>
          <UsersAutocomplete
            areAllUsersAdded={areAllUsersAdded}
            hasAddAllUsersOption={true}
            scrollableTags={true}
            hasFormItemKey={FORM.HAS_FORM}
            isDirty={formMethods.getValues().users?.length}
            newItemId={FORM.NEW_ITEM_ID}
            newItemWithoutLoginId={FORM.NEW_ITEM_WITHOUT_LOGIN_ID}
            optionsData={users}
          />
        </ActionModal>
      </form>
    </FormProvider>
  );
}

AccessGroupAddUsersModal.propTypes = {
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  open: PropTypes.bool.isRequired,
};

export default AccessGroupAddUsersModal;
